import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useMutation } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

interface GenerateDesignEmbeddingsRequest {
  projectUuid: string;
}

interface GenerateDesignEmbeddingsResponse {
  success: boolean;
  message: string;
  data: {
    projectUuid: string;
  };
}

/**
 * Regenerate embeddings for the design ontology graph (v2)
 */
async function generateDesignEmbeddings(
  data: GenerateDesignEmbeddingsRequest,
  token: string
): Promise<GenerateDesignEmbeddingsResponse> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/design-ontology-graph/v2/${data.projectUuid}/regenerate-embeddings?regenerate=true`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to regenerate design embeddings.");
  }

  return response.json();
}

/**
 * Hook to regenerate design ontology embeddings for a project
 */
export function useGenerateDesignEmbeddings(
  onSuccess?: (data: GenerateDesignEmbeddingsResponse) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (data: GenerateDesignEmbeddingsRequest) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return generateDesignEmbeddings(data, token);
    },
    onSuccess,
    onError,
  });
}
